import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { X, History as HistoryIcon } from "lucide-react";
import { fmtDateTime } from "@/lib/time";

/**
 * Slide-in drawer that lists every audited change for a single entity.
 * Hits GET /api/audit/entity/{entity}/{id} and renders a timeline with the
 * field-level diff, who changed it, when, and the mandatory remarks.
 */
export default function HistoryDrawer({ open, onClose, entity, entityId, title }) {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!open || !entity || !entityId) return;
        setLoading(true);
        api.get(`/audit/entity/${entity}/${entityId}`)
            .then(r => setItems(r.data || []))
            .catch(e => toast.error(apiError(e)))
            .finally(() => setLoading(false));
    }, [open, entity, entityId]);

    if (!open) return null;

    return (
        <div className="fixed inset-0 z-50 flex justify-end bg-background/70 backdrop-blur-sm" onClick={onClose} data-testid="history-drawer">
            <div onClick={e => e.stopPropagation()} className="w-full sm:max-w-lg bg-muted border-l border-border h-full flex flex-col">
                <div className="flex items-center justify-between px-6 py-4 border-b border-border">
                    <div className="flex items-center gap-2">
                        <HistoryIcon className="w-4 h-4 text-primary" strokeWidth={1.5} />
                        <div className="font-serif text-xl">Change history</div>
                    </div>
                    <button onClick={onClose} data-testid="history-drawer-close" className="text-muted-foreground hover:text-foreground">
                        <X className="w-5 h-5" strokeWidth={1.5} />
                    </button>
                </div>
                {title && <div className="px-6 pt-3 text-xs text-muted-foreground">{title}</div>}

                <div className="flex-1 overflow-y-auto px-6 py-4">
                    {loading ? (
                        <div className="text-xs text-muted-foreground py-10 text-center">Loading…</div>
                    ) : items.length === 0 ? (
                        <div className="text-xs text-muted-foreground py-10 text-center" data-testid="history-empty">
                            No edits recorded yet. Every change with a mandatory remark will appear here.
                        </div>
                    ) : (
                        <ol className="relative border-l border-border pl-5 space-y-6" data-testid="history-timeline">
                            {items.map((it) => (
                                <li key={it.id} className="relative">
                                    <span className="absolute -left-[26px] top-1 w-2.5 h-2.5 rounded-full bg-primary" />
                                    <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-mono">{fmtDateTime(it.created_at)}</div>
                                    <div className="text-sm mt-0.5">
                                        <span className="text-primary">{it.user_name || it.user_email || "—"}</span>
                                        <span className="text-muted-foreground"> · </span>
                                        <span className="text-muted-foreground">{(it.changes || []).length} field{(it.changes || []).length !== 1 ? "s" : ""}</span>
                                    </div>
                                    <div className="mt-2 bg-background border border-border divide-y divide-border">
                                        {(it.changes || []).map((c, i) => (
                                            <div key={i} className="px-3 py-2 text-xs font-mono">
                                                <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-1">{c.field}</div>
                                                <div className="flex items-start gap-2 flex-wrap">
                                                    <span className="text-red-400/80 break-all">{renderVal(c.old)}</span>
                                                    <span className="text-muted-foreground">→</span>
                                                    <span className="text-emerald-400/80 break-all">{renderVal(c.new)}</span>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                    {it.reason && (
                                        <div className="mt-2 text-xs text-primary italic">
                                            “{it.reason}”
                                        </div>
                                    )}
                                </li>
                            ))}
                        </ol>
                    )}
                </div>
            </div>
        </div>
    );
}

function renderVal(v) {
    if (v === null || v === undefined || v === "") return "—";
    if (typeof v === "object") return JSON.stringify(v).slice(0, 120);
    return String(v).slice(0, 120);
}
